import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App";
import { initCpbBridge } from "./cpbBridge";
import { installCpbSnapshotHijack } from "./cpbSnapshotHijack";
import { markCpbLoaderHidden } from "./viewerLifecycle";
import "./App.css";

const VIEWER_ROOT_ID = "viewer-3d-root";
const LOG_STYLE = "color:#4b9;font-weight:bold;";
const WAIT_TIMEOUT_MS = 30_000;
/** The CPB DOM node we mount the R3F canvas into. */
const MOUNT_SELECTOR = ".cpb-preview-container";

function log(label: string, ...args: unknown[]) {
  console.log(`%c[viewer] ${label}`, LOG_STYLE, ...args);
}

/**
 * Clear any inline sizing styles we wrote in previous builds.
 *
 * Sizing is the host theme / CPB's responsibility — `.cpb-preview-container`
 * and its ancestors get their width/height from CPB's own CSS (plus the
 * `.cpb-layout.height` override in `assets/customproductbuilder.js`). The
 * viewer just fills whatever box the theme gives it via `#viewer-3d-root`
 * (`position: absolute; inset: 0`), so it should never influence what its
 * ancestors measure as their content height.
 *
 * Re-runs on window resize + CPB mount/update events so that if an earlier
 * build of the viewer left a stale inline `height: 100%` on any ancestor,
 * the next pass strips it and lets the natural CSS rule take over.
 */
function fitMountContainer(container: HTMLElement) {
  container.style.removeProperty("transform");
  container.style.removeProperty("overflow");
  container.style.removeProperty("width");
  container.style.removeProperty("height");
  // CPB writes horizontal padding/margin on mobile via inline JS, which
  // pinched the 3D canvas to ~95 % viewport width and left uneven black
  // strips on either side of the mirror. The companion CSS rule in
  // App.css covers theme-cascade gutters; this strips JS-set inline ones.
  container.style.removeProperty("padding-left");
  container.style.removeProperty("padding-right");
  container.style.removeProperty("margin-left");
  container.style.removeProperty("margin-right");

  let el: HTMLElement | null = container.parentElement;
  while (el && !el.classList.contains("cpb-layout")) {
    el.style.removeProperty("width");
    el.style.removeProperty("height");
    el.style.removeProperty("padding-left");
    el.style.removeProperty("padding-right");
    el.style.removeProperty("margin-left");
    el.style.removeProperty("margin-right");
    el = el.parentElement;
  }
}

/**
 * Watch CPB's full-screen preloader (`.cpb-loader.cpb-loader-fixed` — see
 * `assets/customproductbuilder.css`) and log the moment it becomes hidden.
 *
 * CPB's preloader sits on top of everything while it bootstraps. While it's
 * visible our reveal animation plays underneath and is invisible to the user.
 * Logging its hide moment lets us correlate "user can actually see the scene"
 * with the reveal logs and with `CPB_ON_PRODUCT_MOUNTED` from the bridge.
 *
 * Hide detection covers the usual CPB shutdown paths: element removal, a
 * `display:none` inline style, class-level hiding (e.g. `.--hidden`), and
 * opacity collapse.
 */
function watchCpbLoaderLifecycle() {
  const isHidden = (el: HTMLElement): boolean => {
    if (!el.isConnected) return true;
    const cs = window.getComputedStyle(el);
    if (cs.display === "none" || cs.visibility === "hidden") return true;
    if (Number.parseFloat(cs.opacity) < 0.01) return true;
    return false;
  };

  const attach = (el: HTMLElement): boolean => {
    log(`cpb loader found — watching for hide (class="${el.className}")`);

    let hideLogged = false;
    const loaderFoundAt = performance.now();

    const check = () => {
      if (hideLogged) return;
      if (isHidden(el)) {
        hideLogged = true;
        const ms = Math.round(performance.now() - loaderFoundAt);
        log(`cpb loader hidden (${ms}ms after mount)`);
        markCpbLoaderHidden();
        attrObserver.disconnect();
        removalObserver.disconnect();
      }
    };

    // Element still in DOM but changed visibility via style / class.
    const attrObserver = new MutationObserver(check);
    attrObserver.observe(el, { attributes: true, attributeFilter: ["style", "class"] });

    // Element removed from DOM entirely (CPB's cleanest shutdown path).
    const removalObserver = new MutationObserver(() => {
      if (!el.isConnected) check();
    });
    removalObserver.observe(document.body, { childList: true, subtree: true });

    // In case it's already hidden by the time we started observing.
    check();
    return true;
  };

  const existing = document.querySelector<HTMLElement>(".cpb-loader");
  if (existing) {
    attach(existing);
    return;
  }

  log("waiting for cpb loader to appear…");
  const bodyObserver = new MutationObserver(() => {
    const el = document.querySelector<HTMLElement>(".cpb-loader");
    if (el) {
      bodyObserver.disconnect();
      attach(el);
    }
  });
  bodyObserver.observe(document.body, { childList: true, subtree: true });
}

/**
 * Monkey-patch `Konva.Stage.prototype.{draw,batchDraw}` so that stages whose
 * container element sits inside our mount target become no-ops. CPB's 2D
 * preview keeps firing its per-frame render — we hide it via CSS, but without
 * this patch its `requestAnimationFrame` loop still runs and wastes CPU.
 *
 * Filter is surgical — only stages anchored inside `.cpb-preview-container` go
 * dormant. Any other Konva stage on the page (unlikely, but possible from
 * other Shopify apps) keeps working normally.
 *
 * Idempotent: the patch checks a sentinel flag on the prototype so repeated
 * calls are harmless. If `window.Konva` isn't loaded yet we poll every 100 ms
 * for 10 s before giving up (CPB loads its bundle async).
 */
function silenceCpbKonvaStages() {
  type KonvaStageProto = {
    draw?: (...args: unknown[]) => unknown;
    batchDraw?: (...args: unknown[]) => unknown;
    __maytrixCpbSilenced?: boolean;
    container?: () => Element | undefined;
  };
  type KonvaGlobal = { Stage?: { prototype?: KonvaStageProto } };

  const CPB_MOUNT = ".cpb-preview-container";

  const tryPatch = (): boolean => {
    const proto = (window as unknown as { Konva?: KonvaGlobal }).Konva?.Stage?.prototype;
    if (!proto) return false;
    if (proto.__maytrixCpbSilenced) return true;

    const patchMethod = (name: "draw" | "batchDraw") => {
      const orig = proto[name];
      if (typeof orig !== "function") return;
      proto[name] = function (this: KonvaStageProto, ...args: unknown[]) {
        const el = this.container?.();
        if (el instanceof Element && el.closest(CPB_MOUNT)) {
          // Inside our mount target — CPB's preview stage. Skip the draw.
          return this;
        }
        return orig.apply(this, args);
      };
    };

    patchMethod("draw");
    patchMethod("batchDraw");
    proto.__maytrixCpbSilenced = true;
    log("Konva.Stage draw/batchDraw silenced for stages inside " + CPB_MOUNT);
    return true;
  };

  if (tryPatch()) return;

  log("Konva not on window yet — polling for it");
  const iv = window.setInterval(() => {
    if (tryPatch()) window.clearInterval(iv);
  }, 100);
  window.setTimeout(() => window.clearInterval(iv), 10_000);
}

/**
 * Snap `.cpb-layout` to its final height once `.cpb-swiper-main` stops changing
 * size, then disconnect — single-shot, not a persistent observer.
 *
 * Why: `assets/customproductbuilder.js` computes `.cpb-layout.height =
 * .cpb-swiper-main.getBoundingClientRect().height` on `CPB_ON_PRODUCT_MOUNTED`
 * and on `window.resize`. But at mount time the swiper hasn't finished laying
 * out (images / fonts / swiper.js init still pending), so the measured height
 * is too small and `.cpb-layout` locks to it until the user resizes the window.
 *
 * Fix: attach a `ResizeObserver` to the swiper, debounce 300 ms, and when the
 * swiper has been idle for that window we dispatch a synthetic `window.resize`
 * (reusing the theme script's existing listener) + run `fitMountContainer`,
 * then disconnect. No recurring traffic after that — user's real window
 * resizes are still handled by the native `window.resize` listener set up in
 * `mountInto`.
 */
const SWIPER_SETTLE_MS = 300;

function snapLayoutOnceSwiperSettles(container: HTMLElement) {
  const attach = () => {
    const swiper = document.querySelector<HTMLElement>(".cpb-swiper-main");
    if (!swiper) return false;

    log("waiting for .cpb-swiper-main to settle…");
    let settleTimer: number | undefined;
    const ro = new ResizeObserver(() => {
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        ro.disconnect();
        window.dispatchEvent(new Event("resize"));
        fitMountContainer(container);
        log(".cpb-swiper-main settled; layout height synced");
      }, SWIPER_SETTLE_MS);
    });
    ro.observe(swiper);
    return true;
  };

  if (attach()) return;

  // Swiper not in DOM yet — wait for it once, then attach.
  log("waiting for .cpb-swiper-main to appear…");
  const mo = new MutationObserver(() => {
    if (attach()) mo.disconnect();
  });
  mo.observe(document.body, { childList: true, subtree: true });
}

function mountInto(container: HTMLElement) {
  if (container.querySelector(`#${VIEWER_ROOT_ID}`)) {
    log("already mounted, skipping");
    return;
  }

  // Hide every existing child of the mount target (CPB's default Konva canvas,
  // overlays, price badges, etc.) so only our 3D scene draws inside it.
  for (const child of Array.from(container.children)) {
    (child as HTMLElement).style.display = "none";
  }

  const root = document.createElement("div");
  root.id = VIEWER_ROOT_ID;
  container.appendChild(root);

  fitMountContainer(container);
  window.addEventListener("resize", () => fitMountContainer(container));
  document.body.addEventListener("CPB_ON_PRODUCT_MOUNTED", () =>
    fitMountContainer(container),
  );
  document.body.addEventListener("CPB_ON_OPTIONS_UPDATE", () =>
    fitMountContainer(container),
  );
  snapLayoutOnceSwiperSettles(container);
  silenceCpbKonvaStages();
  // Reroute CPB's add-to-cart snapshot from its (hidden, stale) Konva
  // stage to our 3D scene. The uploaded `_image` line item property then
  // points at our render rather than CPB's 2D preview. Order matters: must
  // run *after* silenceCpbKonvaStages so we share the same Konva-readiness
  // poll path; both are idempotent so re-runs are harmless.
  installCpbSnapshotHijack();
  watchCpbLoaderLifecycle();

  initCpbBridge();

  createRoot(root).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );

  log(`mounted into ${MOUNT_SELECTOR}`);
}

function waitForMountTarget() {
  const existing = document.querySelector<HTMLElement>(MOUNT_SELECTOR);
  if (existing) {
    mountInto(existing);
    return;
  }

  log(`waiting for ${MOUNT_SELECTOR}…`);
  const observer = new MutationObserver(() => {
    const el = document.querySelector<HTMLElement>(MOUNT_SELECTOR);
    if (el) {
      observer.disconnect();
      mountInto(el);
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  window.setTimeout(() => {
    if (!document.querySelector(`#${VIEWER_ROOT_ID}`)) {
      observer.disconnect();
      console.warn(
        `%c[viewer] ${MOUNT_SELECTOR} did not appear within ${WAIT_TIMEOUT_MS}ms`,
        LOG_STYLE,
      );
    }
  }, WAIT_TIMEOUT_MS);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", waitForMountTarget);
} else {
  waitForMountTarget();
}
